import { STATUS_OPTIONS, normalizeStatusInput } from "../../../components/StatusChipSelector";
import { resolveChatImageUrl } from "../../../components/MessageHistory/ImageMessage";
import { isAiAgentLogMessage, isPartnerApiLogMessage } from "../../../components/MessageHistory/messageTypeRegistry";
import {
  buildAiAuditCopyBundle,
  formatAiAuditJson,
  resolveMessageAiAudit,
} from "../../../utils/aiAgentAuditDisplay";
import {
  formatAuditJson,
  getPartnerDisplayLabel,
  getWebhookPayloadForDisplay,
  isInboundWebhookAudit,
  resolveMessageApiAudit,
} from "../../../utils/partnerApiAuditDisplay";
import { sanitizePartnerApiAuditForDisplay } from "../../../utils/partnerApiAuditRedact";
import { formatStoredPhoneForInput } from "../../../utils/phone";

const SEXO_LABELS = { 1: "Masculino", 2: "Feminino", 3: "Outro" };

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function stripHtml(html) {
  if (!html) return "";
  return String(html)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+\n/g, "\n")
    .trim();
}

function formatDisplayDate(value) {
  if (!value) return "—";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "0000-00-00 00:00:00") return "—";
    const mysqlRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
    if (mysqlRegex.test(trimmed)) {
      const [datePart, timePart] = trimmed.split(" ");
      const [year, month, day] = datePart.split("-");
      return `${day}/${month}/${year} ${timePart}`;
    }
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR");
}

function formatPhone(value) {
  if (!value) return "";
  return formatStoredPhoneForInput(String(value));
}

function parseTags(str) {
  if (!str || typeof str !== "string") return [];
  return str.split(",").map((t) => t.trim()).filter(Boolean);
}

function pickStatusLabel(status) {
  const normalized = normalizeStatusInput(status);
  return STATUS_OPTIONS.find((s) => s.value === normalized)?.label || "—";
}

function formatCustomFields(fields) {
  if (!fields || typeof fields !== "object") return [];
  return Object.entries(fields)
    .filter(([, v]) => v != null && String(v).trim() !== "")
    .map(([key, value]) => ({
      label: key.replace(/_/g, " "),
      value: Array.isArray(value) ? value.join(", ") : String(value),
    }));
}

function buildDetailRows(entries) {
  return entries
    .filter(([, value]) => value != null && String(value).trim() !== "" && String(value).trim() !== "—")
    .map(
      ([label, value]) =>
        `<tr><th>${escapeHtml(label)}</th><td>${escapeHtml(value)}</td></tr>`
    )
    .join("");
}

function getMessageTimestamp(msg) {
  return (
    msg?.timestamp ||
    msg?.createdAt ||
    msg?.lastUpdate ||
    msg?.updatedAt ||
    msg?.date ||
    null
  );
}

function getMessageSenderLabel(msg, userData, labels) {
  const type = String(msg?.type || "").toLowerCase();
  if (type === "note" || type === "xpro") return labels.internalNote || "Anotação interna";
  if (type === "log") return labels.systemLog || "Registro do sistema";

  const senderType = String(msg?.senderType || "").toLowerCase();
  const sender = String(msg?.sender || "").toLowerCase();

  if (sender === "system" || senderType === "system") {
    return labels.systemLog || "Registro do sistema";
  }

  const isClient =
    sender === "cliente" ||
    sender === "client" ||
    senderType === "client" ||
    senderType === "customer" ||
    msg?.channel === "customer";

  if (isClient) {
    return msg?.senderName || labels.client || "Cliente";
  }

  if (senderType === "ai_agent" || senderType === "chatbot") {
    return msg?.senderName || labels.aiAgent || "Agente de IA";
  }

  const loggedUserId = userData?.id != null ? String(userData.id) : null;
  const msgUserId =
    msg?.senderId != null
      ? String(msg.senderId)
      : msg?.id_usuario != null
        ? String(msg.id_usuario)
        : null;

  if (loggedUserId && msgUserId && loggedUserId === msgUserId) {
    return msg?.senderName || userData?.nome || userData?.name || labels.me || "Eu";
  }

  if (msg?.senderName) return msg.senderName;
  if (senderType === "agent" || senderType === "operator" || sender === "agent") {
    return labels.agent || "Equipe";
  }

  return labels.other || "Outro";
}

function getAttachmentLines(msg) {
  const lines = [];
  const doc = msg?.document;
  if (doc && typeof doc === "object") {
    const name = doc.name || doc.fileName || doc.filename || "Documento";
    const url = doc.url || doc.link || "";
    lines.push(url ? `[Documento: ${name} — ${url}]` : `[Documento: ${name}]`);
  } else if (typeof doc === "string" && doc.trim()) {
    lines.push(`[Documento: ${doc.trim()}]`);
  }

  // Imagens entram como <img> em getMessageImagesHtml — não listar só o link.
  if (msg?.video && String(msg.video).trim()) {
    lines.push(`[Vídeo: ${String(msg.video).trim()}]`);
  }
  if (msg?.audio && String(msg.audio).trim()) {
    lines.push(`[Áudio: ${String(msg.audio).trim()}]`);
  }
  if (msg?.location && typeof msg.location === "object") {
    const lat = msg.location.latitude ?? msg.location.lat;
    const lng = msg.location.longitude ?? msg.location.lng ?? msg.location.lon;
    if (lat != null && lng != null) {
      lines.push(`[Localização: ${lat}, ${lng}]`);
    }
  }

  const type = String(msg?.type || "").toLowerCase();
  if (type === "location" && !lines.some((l) => l.startsWith("[Localização"))) {
    lines.push("[Localização compartilhada]");
  }
  if (type === "contact") {
    lines.push("[Contato compartilhado]");
  }

  return lines;
}

function getMessageImagesHtml(msg) {
  const raw = msg?.image;
  if (!raw || !String(raw).trim()) return "";
  const url = resolveChatImageUrl(String(raw).trim());
  if (!url) return "";
  return `<figure class="print-image"><img src="${escapeHtml(url)}" alt="Imagem da mensagem" loading="eager" /></figure>`;
}

function getMessageBody(msg) {
  const type = String(msg?.type || "").toLowerCase();
  const parts = [];

  const textPlain = stripHtml(msg?.text || msg?.textPlain || "");
  if (textPlain) parts.push(textPlain);

  if (type === "email" && msg?.rawEmail) {
    const subjectMatch = String(msg.rawEmail).match(/^Subject:\s*(.+)$/im);
    if (subjectMatch?.[1]) {
      parts.unshift(`Assunto: ${subjectMatch[1].trim()}`);
    }
  }

  parts.push(...getAttachmentLines(msg));

  const hasImage = Boolean(msg?.image && String(msg.image).trim());

  if (!parts.length) {
    if (hasImage) return "";
    if (type === "log") return stripHtml(msg?.text || "") || "—";
    if (type) return `[${type}]`;
    return "—";
  }

  return parts.join("\n");
}

function auditBlock(title, value, formatJson) {
  const text = formatJson(value);
  if (!text || !String(text).trim()) return "";
  return `<div class="audit-block"><div class="audit-title">${escapeHtml(title)}</div><pre>${escapeHtml(text)}</pre></div>`;
}

function buildPartnerAuditHtml(msg, labels) {
  const raw = resolveMessageApiAudit(msg);
  const audit = raw ? sanitizePartnerApiAuditForDisplay(raw) : null;
  if (!audit) return "";

  const isWebhook = isInboundWebhookAudit(audit);
  const meta = [
    getPartnerDisplayLabel(audit.partner),
    audit.operation,
    audit.direction,
    audit.response?.status != null ? `HTTP ${audit.response.status}` : "",
    audit.durationMs != null ? `${audit.durationMs}ms` : "",
    audit.error ? String(audit.error) : "",
  ]
    .filter((part) => part != null && String(part).trim() !== "")
    .join(" · ");

  const blocks = isWebhook
    ? [
        auditBlock(
          labels.auditWebhook || "JSON recebido (webhook)",
          getWebhookPayloadForDisplay(audit),
          formatAuditJson
        ),
        auditBlock(labels.auditHeaders || "Headers", audit.request?.headers, formatAuditJson),
        auditBlock(
          labels.auditPlatformResponse || "Resposta da plataforma",
          audit.response,
          formatAuditJson
        ),
      ].join("")
    : [
        auditBlock(labels.auditRequest || "Request", audit.request, formatAuditJson),
        auditBlock(labels.auditResponse || "Response", audit.response, formatAuditJson),
      ].join("");

  if (!blocks) return "";
  return `<div class="audit">${meta ? `<div class="audit-meta">${escapeHtml(meta)}</div>` : ""}${blocks}</div>`;
}

function buildAiAuditHtml(msg, labels) {
  const audit = resolveMessageAiAudit(msg);
  if (!audit) return "";

  const meta = [
    audit.category,
    audit.operation || audit.featureKind,
    audit.model,
    audit.skillKey,
    audit.durationMs != null ? `${audit.durationMs}ms` : "",
    audit.error ? String(audit.error) : "",
  ]
    .filter((part) => part != null && String(part).trim() !== "")
    .join(" · ");

  const isRule = audit.category === "internal_rule";
  const blocks = isRule
    ? [
        auditBlock(labels.auditRules || "Regras", audit.rules, formatAiAuditJson),
        auditBlock(labels.auditContext || "Contexto", audit.context, formatAiAuditJson),
      ].join("")
    : [
        auditBlock(labels.auditRequest || "Request", audit.request, formatAiAuditJson),
        auditBlock(labels.auditResponse || "Response", audit.response, formatAiAuditJson),
        audit.usage
          ? auditBlock(labels.auditUsage || "Uso", audit.usage, formatAiAuditJson)
          : "",
      ].join("");

  if (!blocks) {
    const fallback = buildAiAuditCopyBundle(audit);
    if (!fallback) return "";
    return `<div class="audit"><pre>${escapeHtml(fallback)}</pre></div>`;
  }

  return `<div class="audit">${meta ? `<div class="audit-meta">${escapeHtml(meta)}</div>` : ""}${blocks}</div>`;
}

function getTechnicalAuditHtml(msg, labels, options) {
  const parts = [];
  if (options?.includeApiPayloads && isPartnerApiLogMessage(msg)) {
    parts.push(buildPartnerAuditHtml(msg, labels));
  }
  if (options?.includeAiPayloads && isAiAgentLogMessage(msg)) {
    parts.push(buildAiAuditHtml(msg, labels));
  }
  return parts.filter(Boolean).join("");
}

function getMessageBodyHtml(msg, labels = {}, auditOptions = null) {
  const text = getMessageBody(msg);
  const textHtml = text ? escapeHtml(text).replace(/\n/g, "<br/>") : "";
  const imagesHtml = getMessageImagesHtml(msg);
  const auditHtml = getTechnicalAuditHtml(msg, labels, auditOptions);
  const chunks = [textHtml, imagesHtml, auditHtml].filter(Boolean);
  if (chunks.length) return chunks.join("");
  return escapeHtml("—");
}

function buildClientSection(conversation, labels) {
  const sexoRaw = conversation?.sexo;
  const sexoNum = Number(sexoRaw);
  const sexoLabel =
    Number.isFinite(sexoNum) && SEXO_LABELS[sexoNum]
      ? SEXO_LABELS[sexoNum]
      : String(sexoRaw || "").trim();

  const addressParts = [
    conversation?.endereco,
    conversation?.numero,
    conversation?.complemento,
    conversation?.bairro,
    conversation?.cidade,
    conversation?.uf,
    conversation?.cep,
  ]
    .map((v) => (v != null ? String(v).trim() : ""))
    .filter(Boolean);

  const rows = buildDetailRows([
    [labels.name || "Nome", conversation?.nome || conversation?.name],
    [labels.email || "E-mail", conversation?.email],
    [labels.mobile || "Celular", formatPhone(conversation?.celular)],
    [labels.phone || "Telefone", formatPhone(conversation?.telefone || conversation?.phone)],
    [labels.whatsapp || "WhatsApp", formatPhone(conversation?.whatsapp)],
    [labels.cpf || "CPF", conversation?.cpf],
    [labels.identity || "Identidade", conversation?.identidade],
    [
      labels.birthDate || "Data de nascimento",
      conversation?.data_nascimento ? formatDisplayDate(conversation.data_nascimento) : "",
    ],
    [labels.gender || "Sexo", sexoLabel],
    [labels.internalId || "ID interno", conversation?.id_interno],
    [labels.clientType || "Tipo de cliente", conversation?.clientType],
    [labels.facebook || "Facebook", conversation?.id_facebook],
    [labels.instagram || "Instagram", conversation?.id_instagram],
    [labels.linkedin || "LinkedIn", conversation?.id_linkedin],
    [labels.address || "Endereço", addressParts.join(", ")],
    [
      labels.clientTags || "Tags do cliente",
      parseTags(conversation?.clientTags ?? conversation?.client?.tags ?? "").join(", "),
    ],
    ...formatCustomFields(conversation?.clientCustomFields).map(({ label, value }) => [
      label,
      value,
    ]),
  ]);

  if (!rows) {
    return `<p class="empty">${escapeHtml(labels.noClientData || "Sem dados do cliente.")}</p>`;
  }

  return `<table class="details">${rows}</table>`;
}

function buildRequestSection(conversation, labels) {
  const assignee =
    conversation?.agentName ||
    conversation?.userName ||
    conversation?.assignedTo ||
    labels.queue ||
    "Fila";

  const typeChain = [
    conversation?.typeName || conversation?.tipo_nome,
    conversation?.subtype1Name || conversation?.subtipo1_nome,
    conversation?.subtype2Name || conversation?.subtipo2_nome,
    conversation?.subtype3Name || conversation?.subtipo3_nome,
  ]
    .map((v) => (v != null ? String(v).trim() : ""))
    .filter(Boolean)
    .join(" › ");

  const rows = buildDetailRows([
    [labels.protocol || "Protocolo", conversation?.protocol || conversation?.id],
    [labels.status || "Status", pickStatusLabel(conversation?.status)],
    [
      labels.project || "Empresa",
      conversation?.projectName || conversation?.empresa || conversation?.projectId,
    ],
    [
      labels.channel || "Canal",
      conversation?.channelLabel || conversation?.channel || conversation?.channelType,
    ],
    [labels.type || "Tipo", typeChain || conversation?.typeId],
    [labels.assignee || "Responsável", assignee],
    [labels.potential || "Potencial", conversation?.potential ?? conversation?.potencial],
    [
      labels.nextContact || "Próximo contato",
      conversation?.nextContactAt || conversation?.dataproximocontato
        ? formatDisplayDate(conversation.nextContactAt || conversation.dataproximocontato)
        : "",
    ],
    [labels.invoice || "NF", conversation?.nf],
    [labels.product || "Produto", conversation?.produto],
    [labels.campaign || "Campanha", conversation?.campanha],
    [labels.additionalText || "Texto adicional", conversation?.texto_adicional],
    [
      labels.additionalDate || "Data adicional",
      conversation?.data_adicional ? formatDisplayDate(conversation.data_adicional) : "",
    ],
    [labels.value || "Valor", conversation?.valor],
    [
      labels.requestTags || "Tags da solicitação",
      parseTags(conversation?.tags ?? "").join(", "),
    ],
    ...formatCustomFields(conversation?.customFields).map(({ label, value }) => [label, value]),
  ]);

  if (!rows) {
    return `<p class="empty">${escapeHtml(labels.noRequestData || "Sem dados da solicitação.")}</p>`;
  }

  return `<table class="details">${rows}</table>`;
}

function buildMessagesSection(messages, userData, labels, partialHistory, auditOptions) {
  const sorted = [...(messages || [])].sort((a, b) => {
    const ta = new Date(getMessageTimestamp(a) || 0).getTime();
    const tb = new Date(getMessageTimestamp(b) || 0).getTime();
    if (ta !== tb) return ta - tb;
    const idA = Number(a?.id) || 0;
    const idB = Number(b?.id) || 0;
    return idA - idB;
  });

  const filtersNote = labels.filtersNote
    ? `<p class="filters">${escapeHtml(labels.filtersNote)}</p>`
    : "";

  if (!sorted.length) {
    return `${filtersNote}<p class="empty">${escapeHtml(labels.noMessages || "Nenhuma mensagem no histórico.")}</p>`;
  }

  const items = sorted
    .map((msg) => {
      const type = String(msg?.type || "").toLowerCase();
      const isLog = type === "log";
      const isNote = type === "note" || type === "xpro";
      const sender = getMessageSenderLabel(msg, userData, labels);
      const when = formatDisplayDate(getMessageTimestamp(msg));
      const body = getMessageBodyHtml(msg, labels, auditOptions);
      const via = msg?.channelLabel || msg?.channel || msg?.via || "";

      return `
        <article class="message ${isLog ? "message-log" : isNote ? "message-note" : ""}">
          <header>
            <strong>${escapeHtml(sender)}</strong>
            <span>${escapeHtml(when)}</span>
          </header>
          ${via ? `<div class="via">${escapeHtml(labels.via || "Via")}: ${escapeHtml(via)}</div>` : ""}
          <div class="body">${body}</div>
        </article>
      `;
    })
    .join("");

  const partialNote = partialHistory
    ? `<p class="partial">${escapeHtml(
        labels.partialHistory ||
          "Histórico parcial — a impressão alcançou o limite de registros e pode ter omitido mensagens mais antigas."
      )}</p>`
    : "";

  return `${filtersNote}${partialNote}<div class="messages">${items}</div>`;
}

export function buildPrintHtml({
  conversation,
  messages,
  userData,
  labels,
  partialHistory,
  logoUrl,
  printedBy,
  copyright,
  includeApiPayloads = false,
  includeAiPayloads = false,
}) {
  const title = labels.title || "Solicitação";
  const clientName = conversation?.nome || conversation?.name || "";
  const printedAt = formatDisplayDate(new Date());
  const printedByName =
    printedBy || userData?.nome || userData?.name || labels.unknownUser || "—";
  const logoBlock = logoUrl
    ? `<img src="${escapeHtml(logoUrl)}" alt="${escapeHtml(labels.brandName || "Xpro")}" class="logo" />`
    : `<div class="logo-fallback">${escapeHtml(labels.brandName || "Xpro")}</div>`;

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)} ${escapeHtml(conversation?.protocol || conversation?.id || "")}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: "Segoe UI", Arial, sans-serif;
      color: #111;
      margin: 24px;
      font-size: 13px;
      line-height: 1.45;
      min-height: 100vh;
      display: flex;
      flex-direction: column;
    }
    .print-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      border-bottom: 2px solid #e0e0e0;
      padding-bottom: 14px;
      margin-bottom: 18px;
    }
    .print-header .logo {
      height: 42px;
      width: auto;
      max-width: 180px;
      object-fit: contain;
    }
    .logo-fallback {
      font-size: 22px;
      font-weight: 700;
      letter-spacing: 0.02em;
    }
    .print-header-meta {
      text-align: right;
      font-size: 12px;
      color: #555;
      line-height: 1.5;
    }
    .print-header-meta strong {
      color: #111;
    }
    .print-content {
      flex: 1;
    }
    h1 {
      font-size: 20px;
      margin: 0 0 16px;
    }
    h2 {
      font-size: 15px;
      margin: 20px 0 8px;
      padding-bottom: 4px;
      border-bottom: 1px solid #ddd;
    }
    table.details {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 8px;
    }
    table.details th,
    table.details td {
      border: 1px solid #ddd;
      padding: 6px 8px;
      vertical-align: top;
      text-align: left;
    }
    table.details th {
      width: 180px;
      background: #f5f5f5;
      font-weight: 600;
    }
    .messages {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .message {
      border: 1px solid #ddd;
      border-radius: 6px;
      padding: 8px 10px;
      page-break-inside: avoid;
    }
    .message header {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 4px;
      font-size: 12px;
    }
    .message header span {
      color: #666;
      white-space: nowrap;
    }
    .message .via {
      color: #666;
      font-size: 11px;
      margin-bottom: 4px;
    }
    .message .body {
      white-space: pre-wrap;
      word-break: break-word;
    }
    .audit {
      margin-top: 8px;
      font-style: normal;
      font-weight: 400;
      background: #fff;
      border: 1px solid #ccc;
      border-radius: 4px;
      padding: 8px;
      white-space: normal;
    }
    .audit-meta,
    .audit-title {
      font-size: 11px;
      font-weight: 600;
      color: #333;
      margin-bottom: 4px;
    }
    .audit-block + .audit-block {
      margin-top: 8px;
    }
    .audit pre {
      margin: 0;
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      font-size: 10px;
      line-height: 1.4;
      white-space: pre-wrap;
      word-break: break-word;
    }
    .print-image {
      margin: 8px 0 0;
      padding: 0;
      white-space: normal;
    }
    .print-image img {
      display: block;
      max-width: 100%;
      max-height: 420px;
      width: auto;
      height: auto;
      object-fit: contain;
      border: 1px solid #ddd;
      border-radius: 4px;
      background: #fafafa;
    }
    .message-note {
      background: #fff9e6;
      border-color: #f0d98c;
    }
    .message-log {
      background: #f3f3f3;
      border-style: dashed;
      font-style: italic;
    }
    .empty, .partial, .filters {
      color: #666;
      font-style: italic;
    }
    .filters {
      margin: 0 0 12px;
    }
    .partial {
      margin-bottom: 12px;
      color: #a65;
    }
    .print-footer {
      margin-top: 32px;
      padding-top: 12px;
      border-top: 1px solid #ddd;
      text-align: center;
      color: #666;
      font-size: 11px;
    }
    @media print {
      body { margin: 12mm; }
      .print-footer {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 8mm 12mm;
        background: #fff;
      }
    }
  </style>
</head>
<body>
  <header class="print-header">
    ${logoBlock}
    <div class="print-header-meta">
      <div><strong>${escapeHtml(labels.printedBy || "Impresso por")}:</strong> ${escapeHtml(printedByName)}</div>
      <div><strong>${escapeHtml(labels.printedAt || "Impresso em")}:</strong> ${escapeHtml(printedAt)}</div>
    </div>
  </header>

  <main class="print-content">
    <h1>${escapeHtml(title)}${clientName ? ` — ${escapeHtml(clientName)}` : ""}</h1>

    <h2>${escapeHtml(labels.clientSection || "Cliente")}</h2>
    ${buildClientSection(conversation, labels)}

    <h2>${escapeHtml(labels.requestSection || "Solicitação")}</h2>
    ${buildRequestSection(conversation, labels)}

    <h2>${escapeHtml(labels.messagesSection || "Mensagens")}</h2>
    ${buildMessagesSection(messages, userData, labels, partialHistory, {
      includeApiPayloads,
      includeAiPayloads,
    })}
  </main>

  <footer class="print-footer">
    ${escapeHtml(copyright || `© ${new Date().getFullYear()} Xpro. Todos os direitos reservados.`)}
  </footer>
</body>
</html>`;
}

/**
 * Abre uma janela formatada para impressão da solicitação, cliente e mensagens.
 */
export function printConversationRequest({
  conversation,
  messages = [],
  userData = null,
  labels = {},
  partialHistory = false,
  logoUrl = "",
  printedBy = "",
  copyright = "",
  includeApiPayloads = false,
  includeAiPayloads = false,
}) {
  if (!conversation) return false;

  const html = buildPrintHtml({
    conversation,
    messages,
    userData,
    labels,
    partialHistory,
    logoUrl,
    printedBy,
    copyright,
    includeApiPayloads,
    includeAiPayloads,
  });

  // Blob URL evita document.write e não usa noopener (que faz window.open retornar null
  // mesmo com a janela aberta, deixando about:blank sem conteúdo).
  const blobUrl = URL.createObjectURL(new Blob([html], { type: "text/html;charset=utf-8" }));
  const printWindow = window.open(blobUrl, "_blank");

  if (!printWindow) {
    URL.revokeObjectURL(blobUrl);
    window.alert(labels.popupBlocked || "Permita pop-ups para imprimir a solicitação.");
    return false;
  }

  printWindow.opener = null;

  const waitForImages = (win) => {
    const images = Array.from(win.document?.images || []);
    if (!images.length) return Promise.resolve();
    return Promise.all(
      images.map(
        (img) =>
          img.complete
            ? Promise.resolve()
            : new Promise((resolve) => {
                const done = () => resolve();
                img.addEventListener("load", done, { once: true });
                img.addEventListener("error", done, { once: true });
              })
      )
    );
  };

  const triggerPrint = async () => {
    try {
      await Promise.race([
        waitForImages(printWindow),
        new Promise((resolve) => setTimeout(resolve, 8000)),
      ]);
      printWindow.focus();
      printWindow.print();
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  };

  if (printWindow.document?.readyState === "complete") {
    setTimeout(triggerPrint, 100);
  } else {
    printWindow.onload = () => setTimeout(triggerPrint, 100);
  }

  return true;
}
